import startCase from 'lodash/startCase';

import {
  Button,
  Caption,
  Icon,
  Input,
  LoadingIcon,
  Stack,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { PdfPreview } from '../PdfPreview';

export type ReviewField = {
  name: string;
  type: 'text' | 'checkbox' | 'radio' | 'dropdown';
  /** User-facing label from the PDF; absent for fillable PDFs without one. */
  label?: string;
};

// Prefer the PDF's own label; otherwise humanize the normalized key
// (patient_name -> "Patient Name"), leaving already-human names untouched.
const fieldLabel = ({ name, label }: ReviewField): string =>
  label?.trim() || (name.includes(' ') ? name : startCase(name));

export type ReviewFillableFieldsViewProps = {
  fileName: string;
  fields: ReviewField[];
  alreadyFillable: boolean;
  previewUrl: string | null;
  name: string;
  onNameChange: (name: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
};

export const ReviewFillableFieldsView: React.FC<
  ReviewFillableFieldsViewProps
> = ({
  fileName,
  fields,
  alreadyFillable,
  previewUrl,
  name,
  onNameChange,
  onSave,
  onCancel,
  isSaving,
}) => {
  const { t } = useTranslation();
  const saveDisabled = !name.trim() || isSaving;
  const fieldsLabel =
    fields.length === 1
      ? t('appointmentTools.documents.fieldsCountOne', { count: fields.length })
      : t('appointmentTools.documents.fieldsCountMany', {
          count: fields.length,
        });
  const formFieldsLabel =
    fields.length === 1
      ? t('appointmentTools.documents.formFieldsCountOne', {
          count: fields.length,
        })
      : t('appointmentTools.documents.formFieldsCountMany', {
          count: fields.length,
        });

  return (
    <Stack gap={4}>
      <Stack
        className="rounded-lg bg-bg-indigo-subtle-primary px-4 py-3"
        direction="horizontal"
        gap={2}
        align="center"
      >
        <Icon name="CheckCircle2" size="sm" className="text-text-indigo" />
        <Text size="sm">
          {alreadyFillable
            ? t('appointmentTools.documents.alreadyFillableForm', {
                fileName,
                fields: fieldsLabel,
              })
            : t('appointmentTools.documents.addedFields', {
                fields: formFieldsLabel,
                fileName,
              })}
        </Text>
      </Stack>

      <Stack direction="horizontal" gap={4} align="start">
        <div className="min-w-0 flex-1">
          {previewUrl ? (
            <PdfPreview
              file={previewUrl}
              className="max-h-[55vh]"
              highlightFields
            />
          ) : (
            <Stack align="center" justify="center" className="p-6">
              <LoadingIcon size="lg" />
            </Stack>
          )}
        </div>

        <Stack gap={2} className="w-56 shrink-0">
          <Caption size="2xs" weight="bold" color="tertiary">
            {t('appointmentTools.documents.detectedFields', {
              count: fields.length,
            })}
          </Caption>
          <Stack gap={1} className="max-h-[55vh] overflow-y-auto pr-1">
            {fields.map((field, i) => (
              <Stack
                key={`${field.name}-${i}`}
                direction="horizontal"
                gap={2}
                align="start"
                justify="between"
              >
                <Text size="sm" className="min-w-0 flex-1">
                  {fieldLabel(field)}
                </Text>
                <span className="mt-0.5 shrink-0 rounded-full bg-bg-tertiary px-2 py-0.5">
                  <Caption size="2xs" color="tertiary" inline>
                    {field.type}
                  </Caption>
                </span>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>

      <Stack gap={1}>
        <Stack direction="horizontal" gap={2} align="center">
          <Text size="xs" color="secondary">
            {t('appointmentTools.documents.templateName')}
          </Text>
          {!name.trim() && (
            <Text inline size="xs" className="text-text-negative">
              {t('appointmentTools.documents.required')}
            </Text>
          )}
        </Stack>
        <Input
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={t('appointmentTools.documents.namePlaceholder')}
        />
      </Stack>

      <Stack direction="horizontal" justify="between" align="center" gap={2}>
        <Button variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button onClick={onSave} disabled={saveDisabled}>
          {isSaving
            ? t('appointmentTools.documents.saving')
            : t('appointmentTools.documents.saveAsTemplate')}
        </Button>
      </Stack>
    </Stack>
  );
};
